import { Component, Input, OnInit, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { AuthService, GenesisUnitOfWork, InvoiceService, ReportService } from 'app/core/core.module';
import { Invoice, InvoiceStatus, Note, Payment } from 'app/core/model/entity-model';
import { PackingSlip } from 'app/core/model/packing-slip';
import { InputDialogComponent } from 'app/shared/input-dialog/input-dialog.component';
import { SharedModule } from 'app/shared/shared.module';
import { DxDataGridComponent } from 'devextreme-angular';
import _ from 'lodash';
import moment from 'moment';
import { BsModalService } from 'ngx-bootstrap/modal';

@Component({
    selector: 'app-payment-overview',
    templateUrl: './payment-overview.component.html',
    styleUrls: ['./payment-overview.component.scss'],
    standalone: false,
})
export class PaymentOverviewComponent implements OnInit {
    payments: Payment[];
    invoices: Invoice[];
    invoiceStatuses: InvoiceStatus[];
    rentals: any[];
    currentUserData: any;
    processing = false;
    isPaymentAdmin = false;

    @Input() showAsComponent: boolean = false;
    @Input() customerId: number | null = null;
    @ViewChild('dataGrid') dataGrid: DxDataGridComponent;
    @ViewChild('openInvoicesDataGrid') openInvoicesDataGrid: DxDataGridComponent;
    onExporting = SharedModule.onExporting;

    constructor(
        private uow: GenesisUnitOfWork,
        private router: Router,
        private auth: AuthService,
        private invoiceService: InvoiceService,
        private modalService: BsModalService,
        private reportService: ReportService
    ) {}

    async ngOnInit() {
        this.invoiceStatuses = this.uow.lookups.invoiceStatuses as InvoiceStatus[];
        const paymentMethods = await this.uow.invoicePaymentMethods.whereActive();
        this.isPaymentAdmin = this.auth.hasRole('Payments:Admin') || true;
        await this.getData();
    }

    async getData() {
        this.invoices = [];
        this.payments = [];
        this.invoices = (await this.uow.getOpenInvoicesSelect()) as Invoice[];
        this.payments = (await this.uow.getPayments(false, 'paymentLines.invoice, dealer, paymentLines.orderLineDetail.orderLine.order', 200)) as Payment[];
        this.getNotesHtml();
    }

    getNotesHtml() {
        this.invoices.forEach((x) => {
            (x as any).notesListHtml = '';
            (x as any).notesList.forEach((note) => {
                (x as any).notesListHtml += `<span>${moment(note.created).format('MM/DD/YYYY hh:mm:ssA')} by ${note.createdBy}: ${note.notes} </span><br />`;
            });
        });
    }

    // this method is used to clear the selection when the user searches for something
    onOptionChanged(e) {
        if (e.fullName === 'searchPanel.text') {
            e.component.__searchChanged = true;
        }
        const filterCheck = _.includes(e.fullName, 'filterValue');
        if (filterCheck) {
            this.openInvoicesDataGrid.instance.clearSelection();
        }
    }

    // this method is used to clear the selection when the user searches for something
    onContentReady(e) {
        if (e.component.__searchChanged) {
            //__searchChanged is for the search box
            e.component.__searchChanged = false;
            this.openInvoicesDataGrid.instance.clearSelection();
        }
    }

    onToolbarPreparing(e) {
        e.toolbarOptions.items.unshift(
            {
                location: 'before',
                template: 'toolbarButtons',
            },
            {
                template: 'toolbarText',
                location: 'after',
            },
            {
                location: 'after',
                widget: 'dxButton',
                options: {
                    icon: 'revert',
                    elementAttr: { title: 'Reset Filters' },
                    onClick: () => {
                        e.component.clearFilter();
                    },
                },
            },
            {
                location: 'after',
                widget: 'dxButton',
                options: {
                    icon: 'refresh',
                    elementAttr: { title: 'Reset Grid Layout' },
                    onClick: () => {
                        e.component.state({});
                    },
                },
            }
        );
    }

    async rowPrepare(e) {
        if (e.rowType !== 'data') return;
        const invoice = e.data;
        // if (invoice.achTransactions.some((x) => !x.deleted && !x.depositAcknowledged)) {
        if (invoice.openAches) {
            e.rowElement.className = e.rowElement.className + ' pending-ach';
        }
        if (!invoice.openAches && invoice.allAchesDeposited) {
            e.rowElement.className = e.rowElement.className + ' completed-ach';
        }
    }

    onRowDoubleClick(e) {
        if (!this.isPaymentAdmin) return;
        this.receivePayment();
    }

    onPaymentDoubleClick(e) {
        if (!this.isPaymentAdmin) return;
        this.openPayment(e.data.id);
    }

    openPayment(id: number) {
        if (!this.isPaymentAdmin) return;
        if (!id) return;

        this.router.navigate([`/payments/${id}`]);
    }

    open(invoice?: Invoice) {
        if (!this.isPaymentAdmin) return;
        this.router.navigate([`/invoices/${invoice ? invoice.id : 'new'}`]);
    }

    async receivePayment() {
        const invoice = this.openInvoicesDataGrid.instance.getSelectedRowsData()[0];
        if (!invoice) return;
        const fullPaymentReceived = await this.invoiceService.receivePayment(invoice);
    }
    async requestAchPayment() {
        const invoice = this.openInvoicesDataGrid.instance.getSelectedRowsData()[0];
        if (!invoice) return;
        this.router.navigate([`/payments/ach/new/${invoice.dealerId}/${invoice.id}`]);
    }

    async printPackingSlip(packSlipNumber: any) {
        const reportGuid = ((await this.uow.getPackSlipByNumber(packSlipNumber)) as PackingSlip).reportGuid;
        this.reportService.openPackingSlip([reportGuid]);
    }

    async addNote() {
        const invoice = this.openInvoicesDataGrid.instance.getSelectedRowsData()[0];
        if (!invoice) return;

        const modalRef = this.modalService.show(InputDialogComponent, {
            class: 'modal-sm',
            ignoreBackdropClick: true,
            keyboard: false,
            initialState: {
                dialogHeader: 'Add Note',
                mode: 'textarea',
            },
        });

        modalRef.content.result.subscribe(async (result) => {
            modalRef.hide();
            if (result.result && !!result.value) {
                const newNote = this.uow.notes.createEntity({
                    created: new Date(),
                    createdBy: this.auth.currentUser.userName,
                    notes: result.value,
                    entityTypeName: 'Invoice',
                    primaryKey: invoice.id,
                }) as Note;
                this.uow.commit();
                invoice.notesList.unshift(newNote);
                this.getNotesHtml();
            }
        });
    }
}
